import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface SortableItemProps<T> {
  id: string | number;
  item: T;
  renderItem: (item: T) => React.ReactNode;
}

function SortableItem<T>({ id, item, renderItem }: SortableItemProps<T>) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`bg-white flex items-center gap-3 border-t p-3 ${
        isDragging ? 'shadow border-primary' : 'border-gray-200/80'
      }`}
    >
      {/* 🔥 Drag Handle */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 px-2"
        aria-label="Drag item"
      >
        ☰
      </button>

      {/* Content (inputs work here) */}
      <div className="flex-1">{renderItem(item)}</div>
    </li>
  );
}

export interface DragDropListProps<T> {
  title?: string;
  data: T[];
  isLoading?: boolean;
  getId: (item: T) => string | number;
  renderItem: (item: T) => React.ReactNode;
  onReorder: (items: T[]) => void;
  onSave?: () => void;
  saving?: boolean;
}

function DragDropList<T>({
  title,
  data,
  isLoading = false,
  getId,
  renderItem,
  onReorder,
  onSave,
  saving = false,
}: DragDropListProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // prevents accidental drag while clicking
      },
    }),
  );

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = data.findIndex((item) => getId(item) === active.id);
    const newIndex = data.findIndex((item) => getId(item) === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(data, oldIndex, newIndex));
  };

  return (
    <div className="container-fluid py-3">
      {(title || onSave) && (
        <div className="flex justify-between items-center mb-3">
          {title && <h4 className="mb-0 text-lg font-medium">{title}</h4>}
          {onSave && (
            <button
              className="btn btn-sm btn-primary"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Order'}
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={data.map(getId)}
            strategy={verticalListSortingStrategy}
          >
            <ul className="divide-y">
              {data.map((item) => (
                <SortableItem
                  key={getId(item)}
                  id={getId(item)}
                  item={item}
                  renderItem={renderItem}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

export default DragDropList;
